import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  useToast,
  Button,
  IconButton,
  useBreakpointValue,
  HStack,
  VStack,
  Divider,
  Spinner,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, AddIcon } from '@chakra-ui/icons';
import { FiClock, FiClipboard, FiChevronDown } from 'react-icons/fi';
import { useHistory, useLocation } from 'react-router-dom';

import Calendar from './components/Calendar';
import EventModal from './components/EventModal';
import EventList from './components/EventList';
import AvailabilityManager from './components/AvailabilityManager';
import AppointmentsList from './components/AppointmentsList';
import AllAppointmentsAdmin from './components/AllAppointmentsAdmin';
import NewAppointmentModal from './components/NewAppointmentModal';
import MiniCalendar from 'components/Calendar/MiniCalendar';
import ModalAddSurveyResponse from 'components/Modal/ModalAddSurveyResponse';

import { useModuleCheck, MODULES, useUserAuth, getSiteConfig, canUserAccessCategory } from 'utils/site';
import { getEvents, createEvent, updateEvent, deleteEvent } from 'services/agenda';
import { getTeacherAppointments, getAllAppointments, updateAppointmentStatus, cancelAppointment } from 'services/appointmentBooking';
import { getAllSurveys } from 'services/surveyService';

// ─── Constants ───────────────────────────────────────────────────────────────

const calendarCategories = [
  { value: 'clase',       label: 'Clases',          color: '#2A66B4' },
  { value: 'reunion',     label: 'Reuniones',       color: '#1E7A4E' },
  { value: 'evaluacion',  label: 'Evaluaciones',    color: '#6849C4' },
  { value: 'actividad',   label: 'Actividades',     color: '#946F12' },
  { value: 'importante',  label: 'Importante',      color: '#EF5A2A' },
  { value: 'otro',        label: 'Otros',           color: '#A79C91' },
  { value: 'cita',        label: 'Citas Agendadas', color: '#13C2C2' },
];

const EVENT_PALETTE = {
  clase:      { dot: '#2A66B4', borderColor: '#E2ECFB', btnBg: '#E2ECFB', btnColor: '#2A66B4' },
  reunion:    { dot: '#1E7A4E', borderColor: '#DCF1E6', btnBg: '#DCF1E6', btnColor: '#1E7A4E' },
  evaluacion: { dot: '#6849C4', borderColor: '#ECE6FB', btnBg: '#ECE6FB', btnColor: '#6849C4' },
  actividad:  { dot: '#946F12', borderColor: '#FBEECB', btnBg: '#FBEECB', btnColor: '#946F12' },
  importante: { dot: '#EF5A2A', borderColor: '#FDE7DB', btnBg: '#EF5A2A', btnColor: '#fff' },
  otro:       { dot: '#A79C91', borderColor: '#EFE7DF', btnBg: '#F4EEE7', btnColor: '#6E655C' },
};

const getEventPalette = (event) => {
  if (event.category === 'cita') {
    const status = event._appointment?.status || event.status;
    if (status === 'confirmed' || status === 'completed') return EVENT_PALETTE.clase;
    return EVENT_PALETTE.importante;
  }
  return EVENT_PALETTE[event.category] || EVENT_PALETTE.otro;
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
};

const formatHHMM = (dateStr) => {
  const d = new Date(dateStr);
  return {
    h: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
    m: d.getHours() < 12 ? 'AM' : 'PM',
  };
};

const getDuration = (event) => {
  if (!event.end_date) return null;
  const mins = Math.round((new Date(event.end_date) - new Date(event.start_date)) / 60000);
  if (mins <= 0) return null;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusChip = ({ status }) => {
  const cfg = ({
    pending:   { bg: '#FBEECB', color: '#C77A12', label: 'Pendiente' },
    confirmed: { bg: '#DCF3E7', color: '#1E9E63', label: 'Confirmada' },
    completed: { bg: '#DCF3E7', color: '#1E9E63', label: 'Completada' },
    cancelled: { bg: '#FBE0D9', color: '#D6452B', label: 'Cancelada' },
    scheduled: { bg: '#DCF3E7', color: '#1E9E63', label: 'Programada' },
  })[status] || { bg: '#FBEECB', color: '#C77A12', label: status || 'Pendiente' };

  return (
    <Box as="span" px="10px" py="3px" borderRadius="999px" bg={cfg.bg} color={cfg.color}
      fontSize="11.5px" fontWeight="700" flexShrink={0} whiteSpace="nowrap">
      {cfg.label}
    </Box>
  );
};

const DaySummaryChip = ({ dot, text }) => (
  <HStack spacing={2} px={3} py="8px" borderRadius="999px" bg="white"
    border="1px solid" borderColor="cream.line" flexShrink={0}>
    <Box w="8px" h="8px" borderRadius="50%" bg={dot} flexShrink={0} />
    <Text fontSize="12.5px" fontWeight="700" color="ink.700" whiteSpace="nowrap">{text}</Text>
  </HStack>
);

// ─── Timeline helpers ─────────────────────────────────────────────────────────

const buildTimelineItems = (dayEvents) => {
  const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
  const sorted = [...dayEvents].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

  const shouldShowSlot = (hourMins) =>
    !sorted.some((e) => {
      const d = new Date(e.start_date);
      const eMins = d.getHours() * 60 + d.getMinutes();
      const diff = eMins - hourMins;
      return diff >= -15 && diff < 30;
    });

  const slots = HOURS.filter((h) => shouldShowSlot(h * 60)).map((h) => ({
    type: 'slot',
    h,
    time: h * 60,
    label: `${String(h).padStart(2, '0')}:00`,
  }));

  const evItems = sorted.map((e) => {
    const d = new Date(e.start_date);
    return { type: 'event', event: e, time: d.getHours() * 60 + d.getMinutes() };
  });

  return [...slots, ...evItems].sort((a, b) => a.time - b.time);
};

// ─── Timeline Event Card ──────────────────────────────────────────────────────

const TimelineEventCard = ({ event, onView, onViewStudent, onReschedule, onStartFicha, atencionForms = [], isLast }) => {
  const palette = getEventPalette(event);
  const appt = event._appointment;
  const title = event.title?.replace(/^🗓\s*/, '') || 'Evento';
  const description = event.description || appt?.reason || '';
  const statusValue = appt?.status || event.status;
  const duration = getDuration(event);
  const { h, m } = formatHHMM(event.start_date);
  const teacherName = appt?.teacher_name || '';
  const courseName = appt?.group_name || '';
  const guardianPresent = appt?.guardian_present;
  const studentId = appt?.student_id || appt?.user_id;

  return (
    <Flex gap={{ base: 3, md: '14px' }} py={2} px={{ base: 4, md: 0 }}>
      {/* Time column */}
      <Box flex="0 0 52px" textAlign="right" pt="4px">
        <Text fontWeight="800" fontSize={{ base: '14px', md: '15px' }} lineHeight={1} color="ink.900">{h}</Text>
        <Text fontSize="11px" color="ink.400" fontWeight="700" textTransform="uppercase" mt="1px">{m}</Text>
      </Box>

      {/* Rail */}
      <Flex direction="column" align="center" pt="4px" flexShrink={0}>
        <Box w={{ base: '12px', md: '13px' }} h={{ base: '12px', md: '13px' }} borderRadius="50%"
          border="2.5px solid white" boxShadow={`0 0 0 2.5px ${palette.dot}`} flexShrink={0} />
        {!isLast && <Box w="2px" flex={1} bg="cream.line" mt="5px" minH="20px" />}
      </Flex>

      {/* Body */}
      <Box flex={1} bg="white" border="1px solid" borderColor={palette.borderColor || 'cream.line'}
        borderRadius="16px" p={{ base: '14px 15px', md: '15px 18px' }}
        boxShadow="0 1px 2px rgba(54,38,22,.05)" mb={3}
        transition="box-shadow 0.15s, border-color 0.15s"
        _hover={{ boxShadow: '0 2px 12px rgba(54,38,22,.1)', borderColor: palette.dot }}
        cursor="pointer" onClick={() => onView(event)}
      >
        {/* Top row */}
        <Flex justify="space-between" align="flex-start" gap={3}>
          <Box flex={1} minW={0}>
            <Text fontWeight="800" fontSize={{ base: '14.5px', md: '15px' }} color="ink.900"
              lineHeight={1.2} noOfLines={2}>{title}</Text>
            {description && (
              <Text fontSize="12.5px" color="ink.400" fontWeight="500" mt="4px" noOfLines={1}>
                {description}
              </Text>
            )}
          </Box>
          <Flex direction="column" align="flex-end" gap="6px" flexShrink={0}>
            {statusValue && <StatusChip status={statusValue} />}
            {duration && (
              <HStack spacing={1} color="ink.400">
                <FiClock size="13px" />
                <Text fontSize="12px" fontWeight="700">{duration}</Text>
              </HStack>
            )}
          </Flex>
        </Flex>

        {/* Meta row */}
        {(teacherName || courseName || guardianPresent) && (
          <Flex mt={3} align="center" gap={2} flexWrap="wrap">
            {teacherName && (
              <>
                <Box w="22px" h="22px" borderRadius="50%" bg="#3A66C8" display="flex" alignItems="center"
                  justifyContent="center" fontSize="9px" fontWeight="700" color="white" flexShrink={0}>
                  {getInitials(teacherName)}
                </Box>
                <Text fontSize="12px" color="ink.400" fontWeight="600">{teacherName}</Text>
              </>
            )}
            {courseName && (
              <Box as="span" px="9px" py="3px" borderRadius="999px" bg="#E2ECFB" color="#2A66B4"
                fontSize="11.5px" fontWeight="700">{courseName}</Box>
            )}
            {guardianPresent && (
              <Box as="span" px="9px" py="3px" borderRadius="999px" bg="#ECE6FB" color="#6849C4"
                fontSize="11.5px" fontWeight="700">Apoderado/a presente</Box>
            )}
          </Flex>
        )}

        {/* Actions */}
        <Flex mt={3} gap={2} flexWrap="wrap">
          {event.category === 'cita' && atencionForms.length > 0 ? (
            <Menu placement="bottom-start" isLazy>
              <Tooltip label="Iniciar ficha de atención">
                <MenuButton
                  as={Button}
                  size="sm"
                  borderRadius="9px"
                  fontWeight="700"
                  fontSize="13px"
                  bg={palette.btnBg}
                  color={palette.btnColor}
                  _hover={{ opacity: 0.88 }}
                  rightIcon={<Icon as={FiChevronDown} boxSize="14px" />}
                  onClick={(e) => e.stopPropagation()}
                >
                  Iniciar ficha
                </MenuButton>
              </Tooltip>
              <Portal>
                <MenuList zIndex="popover" fontSize="13px" minW="220px" maxW="280px" onClick={(e) => e.stopPropagation()}>
                  {atencionForms.map((survey) => (
                    <MenuItem
                      key={survey.id}
                      icon={<Icon as={FiClipboard} boxSize="14px" />}
                      onClick={() => onStartFicha(event, survey)}
                    >
                      <VStack align="flex-start" spacing={0}>
                        <Text fontWeight="600" fontSize="13px">{survey.name}</Text>
                        {survey.description && (
                          <Text fontSize="11px" color="ink.400" noOfLines={1}>{survey.description}</Text>
                        )}
                      </VStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          ) : (
            <Button size="sm" borderRadius="9px" fontWeight="700" fontSize="13px"
              bg={palette.btnBg} color={palette.btnColor} _hover={{ opacity: 0.88 }}
              onClick={(e) => { e.stopPropagation(); onView(event); }}
            >
              {event.category === 'cita' ? 'Iniciar ficha →' : 'Ver evento'}
            </Button>
          )}
          {studentId && (
            <Button size="sm" borderRadius="9px" fontWeight="700" fontSize="13px"
              bg="cream.bg2" color="ink.400" _hover={{ bg: 'cream.line', color: 'ink.700' }}
              onClick={(e) => { e.stopPropagation(); onViewStudent(event); }}
            >
              Ver alumno
            </Button>
          )}
          {event.category === 'cita' && onReschedule && (
            <Button size="sm" borderRadius="9px" fontWeight="700" fontSize="13px"
              bg="cream.bg2" color="ink.400" _hover={{ bg: 'cream.line', color: 'ink.700' }}
              onClick={(e) => { e.stopPropagation(); onReschedule(event); }}
            >
              Reagendar
            </Button>
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

// ─── Timeline View ────────────────────────────────────────────────────────────

const TimelineView = ({ events, date, loading, onEventClick, onViewStudent, onReschedule, onStartFicha, atencionForms }) => {
  const dayEvents = events.filter((e) => {
    if (e.all_day) return false;
    const d = new Date(e.start_date);
    return (
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
  });

  const items = buildTimelineItems(dayEvents);
  const isToday = new Date().toDateString() === date.toDateString();
  const dateLabel = date.toLocaleDateString('es-CL', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner color="coral.500" size="md" thickness="2px" />
      </Flex>
    );
  }

  return (
    <Box px={{ base: 0, md: 7 }} py={{ base: 4, md: 5 }}>
      {/* Day label */}
      <Flex align="center" gap={3} px={{ base: 4, md: 0 }} pb={3} flexWrap="wrap">
        {isToday && (
          <Box as="span" px="9px" py="3px" borderRadius="999px" bg="coral.500" color="white"
            fontSize="11px" fontWeight="800" textTransform="uppercase" letterSpacing="0.04em"
            flexShrink={0}>
            Hoy
          </Box>
        )}
        <Text fontSize="12px" fontWeight="800" textTransform="uppercase" color="ink.400"
          letterSpacing="0.06em">
          {dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)}
        </Text>
        <Box flex={1} h="1px" bg="cream.line" />
      </Flex>

      {items.length === 0 ? (
        <Box px={{ base: 4, md: 0 }} py={10} textAlign="center">
          <Text color="ink.400" fontSize="14px" fontWeight="500">Sin eventos para este día</Text>
        </Box>
      ) : (
        items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          if (item.type === 'slot') {
            return (
              <Flex key={`slot-${item.h}`} gap={{ base: 3, md: '14px' }} py="6px"
                px={{ base: 4, md: 0 }} opacity={0.45}>
                <Box flex="0 0 52px" textAlign="right" pt="3px" fontSize="13px"
                  fontWeight="700" color="ink.400">{item.label}</Box>
                <Flex direction="column" align="center" pt="7px" flexShrink={0}>
                  <Box w="7px" h="7px" borderRadius="50%" bg="cream.line2" />
                  {!isLast && <Box w="2px" flex={1} bg="cream.line" mt="3px" minH="14px" />}
                </Flex>
                <Box flex={1} borderBottom="1px dashed" borderColor="cream.line" minH="28px" />
              </Flex>
            );
          }
          return (
            <TimelineEventCard
              key={item.event.id || idx}
              event={item.event}
              onView={onEventClick}
              onViewStudent={onViewStudent}
              onReschedule={onReschedule}
              onStartFicha={onStartFicha}
              atencionForms={atencionForms}
              isLast={isLast}
            />
          );
        })
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

function Agenda() {
  const { user } = useUserAuth();
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const moduleEnabled = useModuleCheck(MODULES.AGENDA);
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const isAdmin = user?.role === 'administrador';

  // Data state
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [privateEvents, setPrivateEvents] = useState([]);

  // Modal state
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [showNewCitaModal, setShowNewCitaModal] = useState(false);

  // View state
  const [currentView, setCurrentView] = useState('day');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [miniCalDate, setMiniCalDate] = useState(new Date());

  // Calendar visibility
  const [showPublicEvents, setShowPublicEvents] = useState(true);
  const [showPrivateEvents, setShowPrivateEvents] = useState(true);
  const [showAppointments, setShowAppointments] = useState(true);

  // Formularios de "Ficha de atención" disponibles (para el dropdown "Iniciar ficha")
  const [atencionForms, setAtencionForms] = useState([]);
  const [atencionTarget, setAtencionTarget] = useState(null); // { customer, survey }

  useEffect(() => {
    if (!user?.role || !canUserAccessCategory(user.role, 'atencion')) return;

    const loadAtencionForms = async () => {
      try {
        const siteConfig = JSON.parse(getSiteConfig() || '{}');
        const glosario = {
          'Pagos CapitalWeb CL': 'pagos-capitalweb-cl',
          'Pagos BlasPascal CL': 'blaspascal',
          'Pagos Testing': 'blaspascal'
        };
        const siteSlug = glosario[siteConfig.name] || 'blaspascal';
        const surveys = await getAllSurveys(siteSlug, { userAuth: user });
        setAtencionForms((surveys || []).filter(survey => survey.category === 'atencion'));
      } catch (error) {
        setAtencionForms([]);
      }
    };

    loadAtencionForms();
  }, [user]);

  // Construye el "customer" que espera ModalAddSurveyResponse a partir de la cita agendada
  const handleStartFicha = useCallback((event, survey) => {
    const appt = event._appointment;
    if (!appt) return;
    const [firstName, ...rest] = (appt.student_name || appt.student_rut || '').split(' ');
    const customer = {
      id: appt.student_id,
      username: appt.student_rut,
      first_name: firstName || appt.student_rut,
      last_name: rest.join(' '),
      email: appt.guardian_email,
      meta_data: [],
    };
    setAtencionTarget({ customer, survey });
  }, []);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadEvents = useCallback(async () => {
    if (!moduleEnabled) return;
    setLoading(true);
    try {
      const response = await getEvents({});
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error loading events:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los eventos', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [moduleEnabled, toast]);

  const loadPrivateEvents = useCallback(async () => {
    if (!moduleEnabled || !user?.id) return;
    try {
      const response = await getEvents({ visibility: 'private', user_id: user.id });
      setPrivateEvents(response.data || []);
    } catch (error) {
      console.error('Error loading private events:', error);
    }
  }, [moduleEnabled, user?.id]);

  const loadAppointments = useCallback(async (date) => {
    if (!moduleEnabled || !user?.id) return;
    const d = date || currentDate;
    const dateFrom = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
    const dateTo   = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
    try {
      // Administrador: ve las citas de TODOS los profesores como contenido principal del calendario
      // (mismo origen de datos que la pestaña "Todas las citas"), no solo las propias.
      const rawList = isAdmin
        ? (await getAllAppointments({ dateFrom, dateTo, perPage: 200 }))?.data || []
        : (await getTeacherAppointments(user.id, { dateFrom, dateTo }))?.data || [];

      const apptEvents = rawList
        .filter((a) => a.status !== 'cancelled')
        .map((a) => ({
          id:           `appt-${a.id}`,
          title:        `🗓 ${a.student_name || a.guardian_name || 'Sin nombre'}`,
          description:  isAdmin && a.teacher_name ? `Con ${a.teacher_name}` : undefined,
          location:     isAdmin && a.teacher_name ? `Prof. ${a.teacher_name}` : undefined,
          start_date:   `${a.appointment_date}T${a.start_time}`,
          end_date:     `${a.appointment_date}T${a.end_time}`,
          category:     'cita',
          status:       a.status,
          all_day:      false,
          _appointment: a,
        }));
      setAppointments(apptEvents);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  }, [moduleEnabled, user?.id, isAdmin]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadPrivateEvents(); }, [loadPrivateEvents]);
  useEffect(() => { loadAppointments(currentDate); }, [loadAppointments, currentDate.getFullYear(), currentDate.getMonth()]);
  useEffect(() => { setMiniCalDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)); }, [currentDate]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const visibleEvents = useMemo(() => [
    ...(showPublicEvents  ? events        : []),
    ...(showPrivateEvents ? privateEvents : []),
    ...(showAppointments  ? appointments  : []),
  ], [events, privateEvents, appointments, showPublicEvents, showPrivateEvents, showAppointments]);

  const currentViewRange = useMemo(() => {
    const d = currentDate;
    if (currentView === 'month') {
      return { start: new Date(d.getFullYear(), d.getMonth(), 1), end: new Date(d.getFullYear(), d.getMonth() + 1, 0) };
    }
    if (currentView === 'week') {
      const dow = (d.getDay() + 6) % 7;
      const mon = new Date(d); mon.setDate(d.getDate() - dow);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return { start: mon, end: sun };
    }
    return { start: d, end: d };
  }, [currentDate, currentView]);

  const todaySummary = useMemo(() => {
    const today = new Date();
    const todayEvts = visibleEvents.filter((e) => {
      const d = new Date(e.start_date);
      return d.toDateString() === today.toDateString();
    });
    const confirmed = todayEvts.filter((e) => ['confirmed', 'completed', 'scheduled'].includes(e._appointment?.status || e.status)).length;
    const pending   = todayEvts.filter((e) => (e._appointment?.status || e.status) === 'pending').length;
    const now = new Date();
    const next = todayEvts
      .filter((e) => new Date(e.start_date) > now)
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))[0] || null;
    return { total: todayEvts.length, confirmed, pending, next };
  }, [visibleEvents]);

  const groupedUpcoming = useMemo(() => {
    const now = new Date();
    const end = new Date(now);
    end.setDate(now.getDate() + 14);
    const upcoming = visibleEvents
      .filter((e) => { const s = new Date(e.start_date); return s > now && s <= end; })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
      .slice(0, 12);
    const groups = {};
    for (const e of upcoming) {
      const key = new Date(e.start_date).toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(e);
    }
    return groups;
  }, [visibleEvents]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCreateEvent = () => { setSelectedEvent(null); setModalMode('create'); setShowEventModal(true); };
  const handleEditEvent   = (event) => { setSelectedEvent(event); setModalMode('edit'); setShowEventModal(true); };
  const handleViewEvent   = (calEvent) => { setSelectedEvent(calEvent.resource || calEvent); setModalMode('view'); setShowEventModal(true); };

  // Deep-link desde otras vistas (ej. "Agenda de hoy" del dashboard): /admin/agenda?cita=ID&fecha=YYYY-MM-DD
  // Abre directamente el detalle de esa cita, sin importar de qué profesor sea.
  useEffect(() => {
    if (!moduleEnabled) return;

    const params = new URLSearchParams(location.search);
    const citaId = params.get('cita');
    const fecha = params.get('fecha');
    if (!citaId || !fecha) return;

    // Limpiar los query params de inmediato para no reabrir el modal en un refresh/back
    history.replace(location.pathname);

    setCurrentDate(new Date(`${fecha}T00:00:00`));
    setCurrentView('day');

    (async () => {
      try {
        const res = await getAllAppointments({ dateFrom: fecha, dateTo: fecha, perPage: 100 });
        const apt = (res?.data || []).find((a) => String(a.id) === String(citaId));
        if (!apt) {
          toast({ title: 'Cita no encontrada', description: 'La cita ya no existe o fue movida.', status: 'warning', duration: 4000, isClosable: true });
          return;
        }
        handleViewEvent({
          id: `appt-${apt.id}`,
          title: `🗓 ${apt.student_name || apt.guardian_name || 'Sin nombre'}`,
          start_date: `${apt.appointment_date}T${apt.start_time}`,
          end_date: `${apt.appointment_date}T${apt.end_time}`,
          category: 'cita',
          status: apt.status,
          all_day: false,
          _appointment: apt,
        });
      } catch (err) {
        console.error('Error abriendo cita desde deep-link:', err);
        toast({ title: 'Error', description: 'No se pudo cargar la cita', status: 'error', duration: 4000, isClosable: true });
      }
    })();
    // Solo debe ejecutarse una vez al montar (los query params se limpian enseguida)
  }, [moduleEnabled]); // eslint-disable-line

  const handleViewStudent = useCallback((event) => {
    const appt = event._appointment;
    const studentId = appt?.student_id || appt?.user_id;
    if (studentId) history.push(`/admin/usuario/${studentId}`);
  }, [history]);

  const handleReschedule = useCallback((event) => {
    setSelectedEvent(event.resource || event);
    setModalMode('edit');
    setShowEventModal(true);
  }, []);

  const handleSaveEvent = async (eventData) => {
    try {
      setLoading(true);
      if (modalMode === 'create') {
        await createEvent(eventData);
        toast({ title: 'Éxito', description: 'Evento creado correctamente', status: 'success', duration: 3000, isClosable: true });
      } else {
        await updateEvent(selectedEvent.id, eventData);
        toast({ title: 'Éxito', description: 'Evento actualizado correctamente', status: 'success', duration: 3000, isClosable: true });
      }
      setShowEventModal(false);
      if (eventData.visibility === 'private') { loadPrivateEvents(); } else { loadEvents(); }
    } catch (error) {
      console.error('Error saving event:', error);
      toast({ title: 'Error', description: 'No se pudo guardar el evento', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      setLoading(true);
      await deleteEvent(eventId);
      toast({ title: 'Éxito', description: 'Evento eliminado correctamente', status: 'success', duration: 3000, isClosable: true });
      setShowEventModal(false);
      loadEvents();
      loadPrivateEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({ title: 'Error', description: 'No se pudo eliminar el evento', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleAppointmentStatusChange = async (appointment, newStatus, notes = '') => {
    try {
      if (newStatus === 'cancelled') {
        await cancelAppointment(appointment.id, notes);
      } else {
        await updateAppointmentStatus(appointment.id, newStatus, notes);
      }
      const labels = { confirmed: 'confirmada', completed: 'completada', cancelled: 'cancelada' };
      toast({ title: `Cita ${labels[newStatus] || 'actualizada'}`, status: 'success', duration: 3000, isClosable: true });
      setShowEventModal(false);
      loadAppointments(currentDate);
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 3000, isClosable: true });
      throw error;
    }
  };

  const handleSaveTeacherNotes = async (appointment, notes) => {
    try {
      await updateAppointmentStatus(appointment.id, appointment.status, notes);
      toast({ title: 'Nota guardada', status: 'success', duration: 2500, isClosable: true });
      loadAppointments(currentDate);
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 3000, isClosable: true });
      throw error;
    }
  };

  const handleNavigate = (action) => {
    const newDate = new Date(currentDate);
    if (action === 'today') { setCurrentDate(new Date()); return; }
    const delta = action === 'prev' ? -1 : 1;
    if (currentView === 'month') newDate.setMonth(newDate.getMonth() + delta);
    else if (currentView === 'week') newDate.setDate(newDate.getDate() + delta * 7);
    else newDate.setDate(newDate.getDate() + delta);
    setCurrentDate(newDate);
  };

  const getDateTitle = () => {
    if (currentView === 'day') {
      const s = currentDate.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    if (currentView === 'week') {
      const dow = (currentDate.getDay() + 6) % 7;
      const mon = new Date(currentDate); mon.setDate(currentDate.getDate() - dow);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      if (mon.getMonth() === sun.getMonth()) {
        return `${mon.getDate()} – ${sun.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
      return `${mon.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })} – ${sun.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    const s = currentDate.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  // ── Module disabled ───────────────────────────────────────────────────────

  if (!moduleEnabled) {
    return (
      <Box maxW="800px" mx="auto" pt="80px" px={4} fontFamily="jakarta">
        <Box bg="white" borderRadius="22px" p={10} textAlign="center"
          border="1px solid" borderColor="cream.line">
          <Text color="ink.400" fontSize="15px">El módulo de Agenda no está habilitado para este sitio.</Text>
        </Box>
      </Box>
    );
  }

  // ── Topbar nav button style ───────────────────────────────────────────────

  const navBtnProps = {
    size: 'sm', variant: 'ghost',
    borderRadius: '10px', border: '1.5px solid', borderColor: 'cream.line2', bg: 'white',
    color: 'ink.400', h: '36px',
    _hover: { bg: 'coral.tint', borderColor: 'coral.500', color: 'coral.700' },
  };

  // ── Right panel calendar visibility items ─────────────────────────────────

  const calVisibility = [
    { label: 'Público',          color: '#2A66B4', value: showPublicEvents,   toggle: () => setShowPublicEvents((v) => !v) },
    { label: 'Privado',          color: '#6849C4', value: showPrivateEvents,  toggle: () => setShowPrivateEvents((v) => !v) },
    { label: 'Citas agendadas',  color: '#13C2C2', value: showAppointments,   toggle: () => setShowAppointments((v) => !v) },
  ];

  const viewOptions = [
    { view: 'day',   label: 'Día' },
    { view: 'week',  label: 'Semana' },
    { view: 'month', label: 'Mes' },
    { view: 'list',             label: 'Lista de eventos' },
    { view: 'appointments',     label: 'Lista de citas' },
    ...(isAdmin ? [{ view: 'all-appointments', label: 'Todas las citas' }] : []),
    { view: 'availability',     label: 'Disponibilidad' },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Flex direction="column" h="calc(100vh - 60px)" pt="60px" overflow="hidden" fontFamily="jakarta" bg="cream.bg">

      {/* ── INNER TOPBAR ── */}
      <Flex h="60px" align="center" gap={{ base: 2, md: 3 }} px={{ base: 4, md: 7 }}
        bg="white" borderBottom="1px solid" borderColor="cream.line" flexShrink={0}>

        {/* Navigation */}
        <HStack spacing={1}>
          <IconButton {...navBtnProps} icon={<ChevronLeftIcon />} aria-label="Anterior"
            onClick={() => handleNavigate('prev')} />
          <Button {...navBtnProps} fontWeight="700" fontSize="13px" px={3}
            onClick={() => handleNavigate('today')}>Hoy</Button>
          <IconButton {...navBtnProps} icon={<ChevronRightIcon />} aria-label="Siguiente"
            onClick={() => handleNavigate('next')} />
        </HStack>

        {/* Date title */}
        <Text fontSize={{ base: '14px', md: '16px' }} fontWeight="800" color="ink.900"
          letterSpacing="-0.01em" textTransform="capitalize" flex={1} minW={0} noOfLines={1}>
          {getDateTitle()}
        </Text>

        {/* Nueva cita */}
        <Button h="38px" px={{ base: 3, md: 4 }} borderRadius="11px" bg="coral.500" color="white"
          fontWeight="700" fontSize={{ base: '13px', md: '13.5px' }}
          leftIcon={<AddIcon boxSize="11px" />}
          boxShadow="0 8px 18px -8px rgba(239,90,42,.65)"
          _hover={{ bg: 'coral.600' }} flexShrink={0}
          onClick={() => setShowNewCitaModal(true)}>
          <Text display={{ base: 'none', md: 'block' }}>Nueva cita</Text>
        </Button>
      </Flex>

      {/* ── VIEW SWITCHER ── */}
      <Flex px={{ base: 4, md: 7 }} py="10px" bg="white" borderBottom="1px solid"
        borderColor="cream.line" flexShrink={0} overflowX="auto"
        sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        <HStack spacing={1} bg="cream.bg2" border="1px solid" borderColor="cream.line"
          borderRadius="12px" p={1} flexShrink={0}>
          {viewOptions.map(({ view, label }) => (
            <Button key={view} h={{ base: '30px', md: '32px' }} px={{ base: '12px', md: '15px' }}
              borderRadius="9px" fontWeight="700" fontSize={{ base: '12.5px', md: '13px' }}
              whiteSpace="nowrap" flexShrink={0}
              bg={currentView === view ? 'white' : 'transparent'}
              color={currentView === view ? 'coral.700' : 'ink.400'}
              boxShadow={currentView === view ? '0 1px 2px rgba(54,38,22,.05)' : 'none'}
              _hover={currentView === view ? {} : { color: 'ink.700' }}
              border="none" onClick={() => setCurrentView(view)}>
              {label}
            </Button>
          ))}
        </HStack>
      </Flex>

      {/* ── DAY SUMMARY STRIP ── */}
      {currentView === 'day' && (
        <Flex px={{ base: 4, md: 7 }} py="10px" bg="cream.bg2" borderBottom="1px solid"
          borderColor="cream.line" align="center" gap={{ base: 2, md: 3 }} flexShrink={0}
          overflowX="auto" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          <DaySummaryChip dot="coral.500"
            text={`${todaySummary.total} ${todaySummary.total === 1 ? 'cita' : 'citas'} hoy`} />
          {todaySummary.confirmed > 0 && (
            <DaySummaryChip dot="ok.500"
              text={`${todaySummary.confirmed} confirmada${todaySummary.confirmed > 1 ? 's' : ''}`} />
          )}
          {todaySummary.pending > 0 && (
            <DaySummaryChip dot="warn.500"
              text={`${todaySummary.pending} pendiente${todaySummary.pending > 1 ? 's' : ''}`} />
          )}
          {todaySummary.next && (
            <HStack spacing={1} flexShrink={0} color="ink.400">
              <FiClock size="14px" />
              <Text fontSize="13px" fontWeight="600" whiteSpace="nowrap">
                Próxima <Text as="b" color="coral.500">{formatHHMM(todaySummary.next.start_date).h}</Text>
              </Text>
            </HStack>
          )}
        </Flex>
      )}

      {/* ── MAIN CONTENT ── */}
      <Flex flex={1} overflow="hidden">

        {/* Calendar / Timeline area */}
        <Box flex={1} overflowY="auto" overflowX="hidden" minW={0}
          pb={{ base: '90px', md: 0 }}
          bg={currentView === 'day' ? 'cream.bg' : 'white'}>
          {currentView === 'day' ? (
            <TimelineView
              events={visibleEvents}
              date={currentDate}
              loading={loading}
              onEventClick={handleViewEvent}
              onViewStudent={handleViewStudent}
              onReschedule={handleReschedule}
              onStartFicha={handleStartFicha}
              atencionForms={atencionForms}
            />
          ) : currentView === 'list' ? (
            <Box p={4}>
              <EventList events={visibleEvents} loading={loading}
                onEventClick={handleViewEvent} onEventEdit={handleEditEvent} onEventDelete={handleDeleteEvent} />
            </Box>
          ) : currentView === 'availability' ? (
            <Box p={4}><AvailabilityManager teacherId={user?.id} currentUser={user} /></Box>
          ) : currentView === 'appointments' ? (
            <Box p={4}><AppointmentsList teacherId={user?.id} currentUser={user} /></Box>
          ) : currentView === 'all-appointments' ? (
            <Box p={4}><AllAppointmentsAdmin /></Box>
          ) : (
            <Calendar
              view={currentView} date={currentDate} events={visibleEvents} loading={loading}
              onNavigate={setCurrentDate} onView={setCurrentView}
              onSelectEvent={handleViewEvent} onSelectSlot={handleCreateEvent}
            />
          )}
        </Box>

        {/* ── RIGHT PANEL (desktop only) ── */}
        {!isMobile && (
          <Box w="300px" minW="300px" borderLeft="1px solid" borderColor="cream.line"
            overflowY="auto" bg="white" flexShrink={0} p={5}>

            {/* Mini calendar */}
            <Text fontSize="12px" fontWeight="800" textTransform="uppercase" color="ink.400"
              letterSpacing="0.06em" mb={3}>
              {miniCalDate.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' }).replace(/^\w/, (c) => c.toUpperCase())}
            </Text>
            <MiniCalendar
              date={miniCalDate}
              selectedDate={currentDate}
              highlightRange={currentViewRange}
              onDayClick={(date) => { setCurrentDate(date); setCurrentView('day'); }}
              onMonthChange={(d) => setMiniCalDate(d)}
            />

            {/* Enlace público de reserva del profesor */}
            {user?.id && (
              <Box mt={4}>
                <Text fontSize="12px" fontWeight="800" textTransform="uppercase" color="ink.400"
                  letterSpacing="0.06em" mb={2}>Mi agenda pública</Text>
                <Box bg="cream.bg" border="1px solid" borderColor="cream.line" borderRadius="10px" p={2}>
                  <Text fontSize="10px" color="ink.400" wordBreak="break-all" mb={2}>
                    {`${window.location.origin}/#/public/reservar-hora/${user.id}`}
                  </Text>
                  <Button
                    size="xs" w="full" leftIcon={<Icon as={FiClipboard} boxSize="12px" />}
                    bg="coral.500" color="white" fontWeight="700"
                    _hover={{ bg: 'coral.600' }} _active={{ bg: 'coral.700' }}
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/#/public/reservar-hora/${user.id}`);
                      toast({ title: 'Enlace copiado al portapapeles', status: 'success', duration: 2000, isClosable: true });
                    }}
                  >
                    Copiar enlace
                  </Button>
                </Box>
              </Box>
            )}

            <Divider my={4} borderColor="cream.line" />

            {/* Calendar visibility */}
            <Text fontSize="12px" fontWeight="800" textTransform="uppercase" color="ink.400"
              letterSpacing="0.06em" mb={3}>Mis Calendarios</Text>
            <VStack spacing={0} align="stretch" mb={4}>
              {calVisibility.map(({ label, color, value, toggle }) => (
                <HStack key={label} spacing={2} px={1} py="7px" borderRadius="8px"
                  cursor="pointer" _hover={{ bg: 'cream.bg' }} onClick={toggle}>
                  <Box w="11px" h="11px" borderRadius="3px" flexShrink={0}
                    bg={value ? color : 'transparent'} border={`2px solid ${color}`}
                    transition="background 0.15s" />
                  <Text fontSize="13.5px" fontWeight="600" color={value ? 'ink.900' : 'ink.400'}
                    transition="color 0.15s">{label}</Text>
                </HStack>
              ))}
            </VStack>

            <Divider mb={4} borderColor="cream.line" />

            {/* Upcoming events */}
            <Text fontSize="12px" fontWeight="800" textTransform="uppercase" color="ink.400"
              letterSpacing="0.06em" mb={3}>Próximas citas</Text>
            {Object.entries(groupedUpcoming).length === 0 ? (
              <Text fontSize="13px" color="ink.400" fontWeight="500">Sin citas próximas</Text>
            ) : (
              <VStack spacing={3} align="stretch">
                {Object.entries(groupedUpcoming).map(([dateLabel, dayEvts]) => (
                  <Box key={dateLabel} bg="cream.bg" border="1px solid" borderColor="cream.line"
                    borderRadius="13px" p={3}>
                    <Text fontSize="11px" fontWeight="800" textTransform="uppercase"
                      letterSpacing="0.06em" color="ink.400" mb={2}>
                      {dateLabel.replace(/^\w/, (c) => c.toUpperCase())}
                    </Text>
                    {dayEvts.map((e, i) => {
                      const pal = getEventPalette(e);
                      const title = e.title?.replace(/^🗓\s*/, '') || 'Evento';
                      const timeStr = formatHHMM(e.start_date).h;
                      return (
                        <Flex key={e.id || i} align="flex-start" gap={2}
                          pt={i === 0 ? 0 : 2} mt={i === 0 ? 0 : 2}
                          borderTop={i === 0 ? 'none' : '1px solid'} borderColor="cream.line"
                          cursor="pointer" onClick={() => handleViewEvent(e)}>
                          <Box w="9px" h="9px" borderRadius="50%" bg={pal.dot} flexShrink={0} mt="4px" />
                          <Box flex={1} minW={0}>
                            <Text fontSize="13.5px" fontWeight="700" color="ink.900" noOfLines={1}>{title}</Text>
                            <Text fontSize="12px" color="ink.400" fontWeight="500" noOfLines={1}>
                              {e.description || e.category || ''}
                            </Text>
                          </Box>
                          <Text fontSize="12px" fontWeight="800" color="ink.400" flexShrink={0}>{timeStr}</Text>
                        </Flex>
                      );
                    })}
                  </Box>
                ))}
              </VStack>
            )}
          </Box>
        )}
      </Flex>

      {/* ── MODALS ── */}
      <EventModal
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        event={selectedEvent}
        mode={modalMode}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        onAppointmentStatusChange={handleAppointmentStatusChange}
        onSaveTeacherNotes={handleSaveTeacherNotes}
        loading={loading}
      />
      <NewAppointmentModal
        isOpen={showNewCitaModal}
        onClose={() => setShowNewCitaModal(false)}
        teacherId={user?.id}
      />
      {atencionTarget && (
        <ModalAddSurveyResponse
          isOpen={!!atencionTarget}
          onClose={() => setAtencionTarget(null)}
          customer={atencionTarget.customer}
          updateSurveyResponses={() => setAtencionTarget(null)}
          directAccess={{ id: atencionTarget.survey.id, slug: atencionTarget.survey.slug }}
        />
      )}
    </Flex>
  );
}

export default Agenda;
